import jwtDecode from 'jwt-decode';

import { ErrorResp } from 'models';
import { LOCALSTORAGE, CLIENT_ID, COGNITO_URL } from 'config';

/**
 * The standard OAuth2 Credentials Payload
 */
export declare namespace API {
    export interface OAuth2Creds {
        access_token: string;
        token_type: string;
        expires_in: number;
        refresh_token?: string;
        scope?: string;
        id_token: string;
    }

    export interface IdentityToken {
        'cognito:groups': UserGroups[];
        'cognito:username': string;
        given_name: string;
        family_name: string;
        email: string;
        email_verified: boolean;
    }
}

export type UserGroups = 'Test';

export interface OAuth2Creds {
    /**
     * Access token required to access secured services
     */
    accessToken: string;
    /**
     * The time in seconds that the access token will last
     */
    expiresIn: number;
    /**
     * The Type of token recieved
     */
    tokenType: string;
    /**
     * The scopes the token has access to
     */
    scopes?: string[];
    /**
     * The refresh token required to get new access tokens
     */
    refreshToken?: string;
    /**
     * The time when the refresh token will expire and must be refreshed
     */
    refreshRefreshTokenTime?: Date;
    /**
     * The time the access token should be refreshed (may be a little before it actually times out)
     */
    refreshTime?: Date;
    idToken: string;
    decodedJWT: IdentityToken;
}

export interface IdentityToken {
    cognitoGroups: UserGroups[];
    cognitoUsername: string;
    givenName: string;
    familyName: string;
    email: string;
    emailVerified: boolean;
}

export interface State {
    path: string;
}

/**
 * A multiplier for when the refresh times should actually attempt to refresh.
 * If above one, expired tokens will continue to be used.
 * If too far below one, tokens will refresh more often than it needs to be.
 * If exactly 1, a token may have a brief window of expirey before it is actually refreshed.
 */
const BUFFER_TIME_BEFORE_EXPIREY = 0.95;

const TOKEN_ROUTE = `${COGNITO_URL}/oauth2/token`;
const AUTHORISATION_ROUTE = `${COGNITO_URL}/oauth2/authorize`;
const LOGOUT_ROUTE = `${COGNITO_URL}/logout`;

const rawIdTokenToInternal = (raw: API.IdentityToken): IdentityToken => ({
    cognitoGroups: raw['cognito:groups'],
    cognitoUsername: raw['cognito:username'],
    email: raw.email,
    emailVerified: raw.email_verified,
    familyName: raw.family_name,
    givenName: raw.given_name,
});

const rawOAuthCredsToInternal = (
    raw: API.OAuth2Creds,
    oldTokens?: OAuth2Creds | null,
): OAuth2Creds => ({
    accessToken: raw.access_token,
    expiresIn: raw.expires_in,
    scopes: raw.scope?.split(' '),
    tokenType: raw.token_type,
    refreshToken: raw.refresh_token ?? oldTokens?.refreshToken,
    idToken: raw.id_token,
    decodedJWT: rawIdTokenToInternal(
        jwtDecode<API.IdentityToken>(raw.id_token),
    ),
    refreshTime: raw.access_token
        ? new Date(
              Date.now() + raw.expires_in * 1000 * BUFFER_TIME_BEFORE_EXPIREY,
          )
        : undefined,
    refreshRefreshTokenTime: raw.refresh_token
        ? new Date(Date.now() + 86_400_000 * BUFFER_TIME_BEFORE_EXPIREY)
        : oldTokens?.refreshRefreshTokenTime,
});

/**
 * Will redirect to the login page with the relevant parameters
 */
export const redirectToAuth = async () => {
    const params = new URLSearchParams({
        client_id: CLIENT_ID,
        response_type: 'code',
        redirect_uri: `${window.location.origin}/`,
        state: JSON.stringify({
            path: `${window.location.pathname}${window.location.search}`,
        }),
    });
    window.location.href = `${AUTHORISATION_ROUTE}?${params.toString()}`;
};

/**
 * Gets a new token along with a refresh token if authCode is provided.
 */
export const getToken = async ({
    authCode,
    tokens,
}: {
    authCode?: string;
    tokens?: OAuth2Creds | null;
}): Promise<OAuth2Creds | ErrorResp> => {
    const params = authCode
        ? new URLSearchParams({
              client_id: CLIENT_ID,
              grant_type: 'authorization_code',
              redirect_uri: `${window.location.origin}/`,
              code: authCode,
          })
        : new URLSearchParams({
              client_id: CLIENT_ID,
              grant_type: 'refresh_token',
              refresh_token: tokens?.refreshToken ?? '',
          });

    if (
        !authCode &&
        tokens?.refreshRefreshTokenTime &&
        tokens.refreshRefreshTokenTime < new Date()
    ) {
        // It's been a day since the refersh token has been received.
        // Best to re-login and get a new one.
        console.log('Refresh Token is out of date: Redirecting to login');
        localStorage.removeItem(LOCALSTORAGE.AUTH);
        redirectToAuth();
    }

    const resp = await fetch(TOKEN_ROUTE, {
        method: 'POST',
        body: params,
        headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
        },
    });

    if (!resp.ok) {
        console.error(
            'An Error occured while trying to refresh your auth token. Redirecting to login page.',
        );
        localStorage.removeItem(LOCALSTORAGE.AUTH);
        redirectToAuth();

        return {
            error: resp.statusText,
            code: resp.status,
        };
    }

    const rawCreds = (await resp.json()) as API.OAuth2Creds;

    return rawOAuthCredsToInternal(rawCreds, tokens);
};

/**
 * Will redirect the page to force a logout
 */
export const logout = async (): Promise<void> => {
    localStorage.removeItem(LOCALSTORAGE.AUTH);

    const params = new URLSearchParams({
        response_type: 'code',
        client_id: CLIENT_ID,
        redirect_uri: `${window.location.origin}/`,
    });
    window.location.href = `${LOGOUT_ROUTE}?${params.toString()}`;
};
